import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Contracts from "@/pages/Contracts";
import ContractDetail from "@/pages/ContractDetail";
import Grants from "@/pages/Grants";
import GrantDetail from "@/pages/GrantDetail";
import ReviewQueue from "@/pages/ReviewQueue";
import Documents from "@/pages/Documents";
import Reports from "@/pages/Reports";
import Import from "@/pages/Import";
import Settings from "@/pages/Settings";
import Escalations from "@/pages/Escalations";
import GranteeManagement from "@/pages/GranteeManagement";
import GranteePortal from "@/pages/GranteePortal";
import BillingPage, { BillingSuccess, BillingCancel } from "@/pages/BillingPage";
import TeamPage from "@/pages/TeamPage";
import AcceptInvite from "@/pages/AcceptInvite";
import Landing from "@/pages/Landing";
import WhatsNew from "@/pages/WhatsNew";
import AuditLog from "@/pages/AuditLog";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setUser(data);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="text-muted-foreground">Signing you in…</p>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading workspace…</p>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "grantee") return <GranteePortal />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
      <Route path="/contracts/:id" element={<Protected><ContractDetail /></Protected>} />
      <Route path="/grants" element={<Protected><Grants /></Protected>} />
      <Route path="/grants/:id" element={<Protected><GrantDetail /></Protected>} />
      <Route path="/queue" element={<Protected><ReviewQueue /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/import" element={<Protected><Import /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/escalations" element={<Protected><Escalations /></Protected>} />
      <Route path="/grantees" element={<Protected><GranteeManagement /></Protected>} />
      <Route path="/team" element={<Protected><TeamPage /></Protected>} />
      <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/billing/cancel" element={<Protected><BillingCancel /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/whats-new" element={<Protected><WhatsNew /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
